import React, { useState } from 'react';
import { AlertTriangle, Check, ChevronLeft, ChevronRight, MoveLeft, Play, ShieldCheck } from 'lucide-react';

/**
 * The proctoring report: verdict banner, two summary tiles, answer authenticity
 * (a bar here, a per-answer list behind "See detailed analysis", each recorded
 * answer playable inline), and the camera and screen checks - flagged ones as
 * rows, clear ones as a grouped checklist. Every view swaps the modal's content;
 * nothing opens on top of it.
 *
 * The verdict is the stored recommendation in the colour getProctoringStatusColor
 * gives it. Only reports written by the answer-analysis pipeline get the
 * reworded titles in ANSWER_VERDICTS, matched on their exact stored
 * actionable_decision: those verdicts come from the answers alone, and "proctoring
 * violations" above the camera rows read as if gaze or phone had caused them.
 * The stored strings are not changed - other code matches on them.
 *
 * Every check is always accounted for - a row if it fired, a line in the clear
 * list if it did not - so a zero never disappears.
 */

const MUTED = 'dark:text-[#A8A8A8] text-textSecondary';
const HEADING = 'dark:text-white text-gray-900 text-sm font-semibold';
const BORDER = 'border dark:border-[#52525280] border-gray-200';
const STRONG = 'dark:text-white text-gray-900';
const CLEAR_HEX = '#3BAA6E';
const BUTTON =
  'shrink-0 px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:text-white text-gray-900 text-sm font-medium hover:bg-gray-50 dark:hover:bg-[rgba(255,255,255,0.05)] transition-colors';
const PURPLE_BUTTON =
  'inline-flex items-center gap-2 rounded-lg border font-medium transition-colors border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 dark:border-[#C9A3FF4D] dark:bg-[#C9A3FF14] dark:text-[#C9A3FF] dark:hover:bg-[#C9A3FF26]';

// Keyed by the actionable_decision strings in
// backend/app/constants/advanced_proctoring.py VERDICT_BY_ACTION.
const ANSWER_VERDICTS = {
  'Multiple responses showed notable signs of scripted or rehearsed delivery. We recommend reviewing the relevant responses.':
    {
      title: 'Major integrity concerns detected',
      body: 'Multiple answers show signs of being read from prepared text. We recommend listening to them before making a decision.',
    },
  'Some responses showed signs of prepared or scripted delivery. The signal is not conclusive, but a quick review may be useful.':
    {
      title: 'Possible integrity concerns detected',
      body: 'Some answers show signs of prepared or scripted delivery. The signal is not conclusive, but a quick review of those answers is recommended.',
    },
  'No notable signs of scripted or rehearsed delivery were detected.': {
    title: 'No integrity concerns detected',
    body: 'No notable signs of read or scripted answers were found.',
  },
};

/**
 * The verdict line to show for a report, here and on the report page's
 * Proctoring Report card, so both always say the same thing.
 */
export const verdictTitle = (report) =>
  ANSWER_VERDICTS[report?.actionable_decision]?.title || report?.recommendation;

// The report's existing palette for answers: red read, amber possible, green
// clear, grey not checked.
const STATUS = {
  read: { hex: '#FF3030', label: 'Likely read' },
  possible: { hex: '#E5A93B', label: 'Possibly read' },
  clear: { hex: '#3BAA6E', label: 'No signs found' },
  not_checked: { hex: '#9CA3AF', label: 'Insufficient evidence' },
};
const ORDER = ['read', 'possible', 'clear', 'not_checked'];
// Statuses worth listing in a filter or legend: "insufficient evidence" only
// when some answer has it, the verdicts always so a zero stays visible.
const shownStatuses = (count) => ORDER.filter((k) => k !== 'not_checked' || count(k) > 0);

// Recruiter wording for the backend's reason codes (advanced_proctoring_service._answer_reason).
const REASON = {
  written_structure: 'Organised like written text rather than speech.',
  flat_delivery: 'Delivered almost without pauses or fillers, which can indicate reading. Not conclusive on its own.',
  too_short: 'Too short to judge.',
  low_score: 'Scored too low to be worth checking.',
  no_recording: 'The recording could not be transcribed.',
  no_speech: 'No speech was found in the recording.',
  failed: 'The analysis failed for this answer.',
};
const reasonText = (a) =>
  REASON[a.reason] || (a.status === 'clear' ? 'No signs of reading found.' : '');

// What a clear check confirms, in plain words. [start, rest]
const CLEAR_TEXT = {
  no_face: ['Face in frame', ' the whole time'],
  multiple_faces: ['One person', ', no one else seen'],
  hands_not_visible: ['Both hands', ' stayed visible'],
  body_not_visible: ['Posture', ' stayed normal'],
  gaze: ['No looking away', ' detected'],
  prohibited_item: ['No prohibited items', ' seen'],
  fullscreen_exit: ['Stayed fullscreen', ' throughout'],
  tab_switch: ['No tab switches', ''],
  multi_monitor_detected: ['One monitor', ' connected'],
  // Only the Windows key - copy/paste is logged as a separate type.
  windows_key_detected: ['Windows key', ' not pressed'],
};
const CAMERA_CHECKS = new Set([
  'no_face',
  'multiple_faces',
  'hands_not_visible',
  'body_not_visible',
  'gaze',
  'prohibited_item',
]);

// The 2026-09-21 release removed hand detection, and until the token fix /log_violation rejected the
// browser events below. Interviews started in between have neither check, so neither may read as clear.
const CHECKS_CHANGED_AT = Date.parse('2026-09-21T17:00:00Z');
const BROWSER_EVENTS_FIXED_AT = null; // release time of the token fix; null while it is not live
const BROWSER_CHECKS = new Set([
  'fullscreen_exit',
  'tab_switch',
  'multi_monitor_detected',
  'windows_key_detected',
]);

/**
 * getProctoringStatusColor hands over a literal Tailwind class such as
 * text-[#FF3030]. The banner tint reuses that hex so the banner always carries
 * the verdict's own colour.
 */
const toneOf = (className) =>
  (className && className.match(/#[0-9A-Fa-f]{6}/)?.[0]) || '#A8A8A8';

// Several detections inside this window read as one episode, not background noise.
const CLUSTER_MINUTES = 5;

/**
 * Parse a stored violation timestamp. The backend writes datetime.utcnow() with no
 * timezone and microsecond precision; Safari rejects more than three fractional
 * digits, and raw Mongo JSON wraps dates in {$date}. Only differences between
 * these values are used, so the missing timezone cancels out.
 */
const toMillis = (ts) => {
  if (ts === null || ts === undefined) return NaN;
  if (typeof ts === 'number') return ts;
  const raw = typeof ts === 'object' && ts.$date ? ts.$date : ts;
  const s = String(raw).replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  return new Date(s).getTime();
};

/**
 * How one type's detections are spread in time. A log entry is the moment a
 * detection fired, not how long it lasted, and questions carry no timestamps -
 * so there is no honest total duration or question range to show, only whether
 * the moments bunch together.
 */
const spreadNote = (logs, type) => {
  const times = (logs || [])
    .filter((l) => l?.violation_type === type && l?.round !== 'coding')
    .map((l) => toMillis(l.timestamp))
    .filter((t) => Number.isFinite(t))
    .sort((a, b) => a - b);
  if (times.length < 2) return null;

  const minutes = (times[times.length - 1] - times[0]) / 60000;
  if (minutes < 1) return 'all within a minute';
  const m = Math.round(minutes);
  if (times.length >= 3 && minutes <= CLUSTER_MINUTES) {
    return `clustered within ${m} min`;
  }
  return `across ${m} min`;
};

const Chip = ({ status }) => (
  <span
    className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap"
    style={{ backgroundColor: `${STATUS[status].hex}1F`, color: STATUS[status].hex }}
  >
    {STATUS[status].label}
  </span>
);

// mt-3: the modal's title sits tight above the report's banner; a back header
// needs more room under the title than the banner does.
const BackHeader = ({ onBack, label, children }) => (
  <div className="flex items-center gap-4 mt-3">
    <button
      type="button"
      onClick={onBack}
      aria-label={label}
      className="flex items-center text-[#C9A3FF] hover:text-purple-400"
    >
      <MoveLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
    </button>
    {children}
  </div>
);

// Every answer with a recording can be played, clean ones included, so a
// reviewer can compare a flagged answer with an unflagged one.
const isPlayable = (a) => !!a.url;

// The modal is a fixed 85vh (EvaluationReport); this is what is left for the
// player once the dialog title, content padding and the header row are taken.
const PLAYER_HEIGHT = 'md:h-[calc(85vh-200px)] md:min-h-[320px]';

const NavButton = ({ answer, children }) => (
  <button
    type="button"
    disabled={!answer}
    onClick={answer?.go}
    className={`${BUTTON} !px-3 !py-1.5 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

/**
 * One answer, played in place of the list. Video and transcript sit side
 * by side at the full height the modal has, so nothing is laid over the picture
 * and no space is left under it. Previous / Next step through answers that
 * have a recording.
 */
const AnswerPlayer = ({ answers, label, onSelect, onBack }) => {
  const playable = answers.filter(isPlayable);
  const i = playable.findIndex((a) => a.label === label);
  const a = playable[i];
  if (!a) return null;
  const step = (other) => other && { go: () => onSelect(other.label), label: other.label };
  const prev = step(playable[i - 1]);
  const next = step(playable[i + 1]);
  const reason = reasonText(a);

  return (
    <div className="flex flex-col gap-4">
      <BackHeader onBack={onBack} label="Back to detailed analysis">
        <h3 className="dark:text-[#C9A3FF] text-textPrimary text-md font-normal shrink-0">
          {a.label}
          {a.isFollowup ? ' (follow-up)' : ''}
        </h3>
        <Chip status={a.status} />
        {reason && (
          <span className={`${MUTED} text-[13px] hidden lg:inline truncate`}>{reason}</span>
        )}
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <NavButton answer={prev}>
            <ChevronLeft className="w-4 h-4" />
            {prev ? prev.label : 'Prev'}
          </NavButton>
          <span className={`${MUTED} text-xs tabular-nums px-1`}>
            {i + 1} / {playable.length}
          </span>
          <NavButton answer={next}>
            {next ? next.label : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </NavButton>
        </div>
      </BackHeader>

      <div className={`grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] ${PLAYER_HEIGHT}`}>
        <video
          key={a.url}
          src={a.url}
          controls
          autoPlay
          playsInline
          className="w-full aspect-video md:aspect-auto md:h-full rounded-xl bg-black object-contain"
        />

        <div className={`${BORDER} rounded-xl px-4 py-3 flex flex-col gap-2 min-h-0 max-h-[50vh] md:max-h-none`}>
          <span className={`${MUTED} text-xs font-medium uppercase tracking-wider`}>
            Answer Transcript
          </span>
          <p className={`${MUTED} text-sm leading-relaxed overflow-y-auto min-h-0 flex-1 pr-1`}>
            {a.transcript || 'No transcript was stored for this answer.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// What the active filter's status means, under the list. "All" shares the
// "Likely read" note: that is the call most likely to be over-read.
const FILTER_NOTE = {
  read: '"Likely read" means the answer is organised like written text. That is not proof of cheating, because a well-prepared candidate can produce the same pattern. Listen before concluding anything.',
  possible: '"Possibly read" means some signs of prepared text were found, but not enough to call it. Worth a listen.',
  clear: '"No signs found" means nothing in the answer pointed to reading.',
  not_checked: '"Insufficient evidence" means the answer could not be judged: it was too short or scored too low to matter. It counts neither for nor against the candidate.',
};

/** The per-answer list behind "See detailed analysis". */
const AnswerAnalysis = ({ answers, filter, setFilter, onBack, onPlay }) => {
  const count = (k) => answers.filter((a) => a.status === k).length;
  const shown = filter === 'all' ? answers : answers.filter((a) => a.status === filter);

  return (
    // At least the modal's content height (85vh less title and padding), so a
    // short list still leaves the note at the bottom edge.
    <div className="flex flex-col gap-4 min-h-[calc(85vh-96px)]">
      <BackHeader onBack={onBack} label="Back to report">
        <h3 className="dark:text-[#C9A3FF] text-textPrimary text-md font-normal">
          Answer Authenticity - Detailed Analysis
        </h3>
      </BackHeader>

      <div className="flex flex-wrap gap-2">
        {[['all', 'All', answers.length], ...shownStatuses(count).map((k) => [k, STATUS[k].label, count(k)])].map(
          ([key, label, n]) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-full border text-[13px] transition-colors ${
                  active
                    ? // The app's selected-chip gradient (CandidateManagementHeader sub-tabs).
                      'border-transparent bg-gradient-to-r from-pink-500 to-blue-500 text-white'
                    : `dark:border-[#52525280] border-gray-200 ${MUTED}`
                }`}
              >
                {label}{' '}
                <span className={`${active ? 'text-white' : STRONG} font-semibold ml-0.5`}>{n}</span>
              </button>
            );
          },
        )}
      </div>

      <div className={`${BORDER} rounded-xl overflow-hidden`}>
        {shown.length === 0 ? (
          <p className={`${MUTED} text-sm px-4 py-4`}>No answers in this group.</p>
        ) : (
          shown.map((a) => (
            <div
              key={a.label}
              className="grid grid-cols-[48px_1fr] sm:grid-cols-[48px_1fr_auto] gap-x-4 gap-y-2 items-center px-4 py-3.5 border-b last:border-b-0 dark:border-[#52525280] border-gray-200"
            >
              <span className={`${MUTED} text-[13px] font-semibold tabular-nums`}>
                {a.label}
              </span>
              <div className="min-w-0">
                <p className={`${STRONG} text-sm leading-snug`}>
                  {a.question || (a.isFollowup ? 'Follow-up question' : 'Question')}
                </p>
                {reasonText(a) && (
                  <p className={`${MUTED} text-[12.5px] mt-1`}>{reasonText(a)}</p>
                )}
              </div>
              <div className="col-start-2 sm:col-start-auto flex items-center gap-3 sm:justify-end">
                <Chip status={a.status} />
                {isPlayable(a) && (
                  <button
                    type="button"
                    onClick={() => onPlay(a.label)}
                    className={`${BUTTON} !px-3 !py-1.5 !text-xs inline-flex items-center gap-1.5`}
                  >
                    <Play className="w-3.5 h-3.5" />
                    Play
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pinned to the bottom of the modal's scroll area so the meaning of the
          active filter stays in view while the list scrolls under it. Opaque so
          rows don't show through; the dark value approximates the dialog's
          translucent dark surface. */}
      <p
        className={`${MUTED} sticky bottom-0 mt-auto -mb-6 pt-3 pb-5 text-xs leading-relaxed bg-white dark:bg-[#0f1226] border-t dark:border-[#52525280] border-gray-200`}
      >
        {FILTER_NOTE[filter] || FILTER_NOTE.read}
      </p>
    </div>
  );
};

const ProctoringSummaryCard = ({
  report,
  proctoringRan,
  titleClassName,
  formatViolationType = (t) => t,
  checkTypes = [],
  interviewStartedAt = '',
  violationCounts = {},
  violationLogs = [],
  screenshotsFor,
  onReview,
  answers = [],
  secondaryCamera = null,
}) => {
  const [view, setView] = useState('report');
  // Kept here so returning from a video lands on the same filtered list.
  const [filter, setFilter] = useState('all');
  const [playing, setPlaying] = useState(null);

  const answerVerdict = ANSWER_VERDICTS[report?.actionable_decision];
  if (!report && !proctoringRan && !secondaryCamera) return null;

  if (view === 'play' && playing) {
    return (
      <AnswerPlayer
        answers={answers}
        label={playing}
        onSelect={setPlaying}
        onBack={() => setView('answers')}
      />
    );
  }

  if (view === 'answers' && answers.length > 0) {
    return (
      <AnswerAnalysis
        answers={answers}
        filter={filter}
        setFilter={setFilter}
        onBack={() => setView('report')}
        onPlay={(label) => {
          setPlaying(label);
          setView('play');
        }}
      />
    );
  }

  const hasEvents = (t) => (violationCounts[t] || 0) > 0;
  const startedAt = Date.parse(interviewStartedAt); // NaN when unknown, so older reports stay unchanged
  const afterChange = startedAt >= CHECKS_CHANGED_AT;
  const browserUnrecorded =
    afterChange && (BROWSER_EVENTS_FIXED_AT === null || startedAt < BROWSER_EVENTS_FIXED_AT);
  const unrecorded =
    proctoringRan && browserUnrecorded
      ? checkTypes.filter((t) => BROWSER_CHECKS.has(t) && !hasEvents(t))
      : [];
  const ranChecks = checkTypes.filter(
    (t) =>
      !unrecorded.includes(t) && !(afterChange && t === 'hands_not_visible' && !hasEvents(t)),
  );

  // Only claim checks are clear when proctoring actually ran.
  const flagged = proctoringRan ? ranChecks.filter(hasEvents) : [];
  const clear = proctoringRan ? ranChecks.filter((t) => !hasEvents(t)) : [];
  const clearGroups = [
    ['Camera', clear.filter((t) => CAMERA_CHECKS.has(t))],
    ['Screen & Browser', clear.filter((t) => !CAMERA_CHECKS.has(t))],
  ].filter(([, list]) => list.length > 0);

  const counts = Object.fromEntries(
    ORDER.map((k) => [k, answers.filter((a) => a.status === k).length]),
  );
  const checked = answers.length - counts.not_checked;

  const title = verdictTitle(report);
  const body = answerVerdict?.body || report?.actionable_decision;
  const tone = toneOf(titleClassName);
  // Same test getProctoringStatusColor uses for its green verdict.
  const VerdictIcon = /no verified violations/i.test(report?.recommendation || '')
    ? ShieldCheck
    : AlertTriangle;

  // The reading score is only meaningful on reports from the answer pipeline.
  const readingScore =
    answerVerdict && typeof report?.plagiarism_score === 'number'
      ? report.plagiarism_score
      : null;
  const showAnswersTile = readingScore !== null;
  const showChecksTile = proctoringRan && checkTypes.length > 0;
  const showChecks =
    flagged.length > 0 || clear.length > 0 || unrecorded.length > 0 || !!secondaryCamera;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Verdict ─────────────────────────────────────────────────────── */}
      {(title || body) && (
        <div
          className="flex items-start gap-3 rounded-xl border px-4 py-3.5"
          style={{ backgroundColor: `${tone}0F`, borderColor: `${tone}33` }}
        >
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${tone}1F`, color: tone }}
          >
            <VerdictIcon className="w-4 h-4" />
          </span>
          <div className="min-w-0 pt-0.5">
            {title && (
              <h3 className={`${titleClassName || MUTED} text-[15px] font-semibold leading-snug`}>
                {title}
              </h3>
            )}
            {body && <p className={`${MUTED} text-sm leading-relaxed mt-1`}>{body}</p>}
          </div>
        </div>
      )}

      {/* ── Summary tiles ───────────────────────────────────────────────── */}
      {(showAnswersTile || showChecksTile) && (
        <div
          className={`grid gap-3 ${showAnswersTile && showChecksTile ? 'sm:grid-cols-2' : ''}`}
        >
          {showAnswersTile && (
            <div className={`${BORDER} rounded-xl px-4 py-3.5 flex flex-col gap-1.5`}>
              <span className={`${MUTED} text-xs font-medium uppercase tracking-wider`}>
                Answers
              </span>
              <span className={`${MUTED} text-[15px] font-medium flex items-baseline gap-2`}>
                <span
                  className="text-2xl font-semibold tabular-nums"
                  style={{ color: tone }}
                >
                  {Math.round(readingScore)}
                </span>
                reading score
              </span>
              <span className={`${MUTED} text-[13px]`}>
                {answers.length > 0
                  ? ORDER.filter((k) => counts[k] > 0)
                      .map((k) => `${counts[k]} ${STATUS[k].label.toLowerCase()}`)
                      .join(' · ')
                  : 'Per-answer breakdown not available for this report'}
              </span>
            </div>
          )}
          {showChecksTile && (
            <div className={`${BORDER} rounded-xl px-4 py-3.5 flex flex-col gap-1.5`}>
              <span className={`${MUTED} text-xs font-medium uppercase tracking-wider`}>
                Camera &amp; Screen
              </span>
              <span className={`${MUTED} text-[15px] font-medium flex items-baseline gap-2`}>
                <span className={`${STRONG} text-2xl font-semibold tabular-nums`}>
                  {flagged.length}
                </span>
                of {ranChecks.length} checks flagged
              </span>
              <span className={`${MUTED} text-[13px]`}>
                {flagged.length > 0
                  ? flagged.map((t) => formatViolationType(t)).join(', ')
                  : 'All checks clear'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Answer Authenticity ─────────────────────────────────────────── */}
      {answers.length > 0 && (
        <section>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-2">
            <h4 className={HEADING}>Answer Authenticity</h4>
            <span className={`${MUTED} text-sm`}>
              · {checked} of {answers.length} answers checked
            </span>
            <button
              type="button"
              onClick={() => setView('answers')}
              className={`${PURPLE_BUTTON} ml-auto px-4 py-2 text-sm`}
            >
              See detailed analysis
            </button>
          </div>

          <div className="flex gap-[3px] h-1.5 rounded-full overflow-hidden mt-4">
            {ORDER.map((k) =>
              counts[k] > 0 ? (
                <div
                  key={k}
                  className="min-w-[4px]"
                  style={{
                    flex: `${counts[k]} 1 0`,
                    backgroundColor: STATUS[k].hex,
                    opacity: k === 'not_checked' ? 0.45 : 0.9,
                  }}
                />
              ) : null,
            )}
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2.5">
            {shownStatuses((k) => counts[k]).map((k) => (
              <span key={k} className="flex items-center gap-1.5 text-xs">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: STATUS[k].hex }}
                />
                <span className={`${STRONG} font-semibold`}>{counts[k]}</span>
                <span className={MUTED}>{STATUS[k].label.toLowerCase()}</span>
              </span>
            ))}
          </div>
        </section>
      )}

      {/* ── Camera & screen checks ──────────────────────────────────────── */}
      {showChecks && (
        <section className={answers.length > 0 ? 'mt-4' : ''}>
          <div className="flex items-center gap-2">
            <h4 className={HEADING}>Camera &amp; Screen Checks</h4>
            {proctoringRan && (
              <span className={`${MUTED} text-sm`}>· {flagged.length} flagged</span>
            )}
          </div>

          <div className="flex flex-col gap-3 mt-3">
            {secondaryCamera &&
              (secondaryCamera.loading ? (
                <div className={`${BORDER} rounded-xl h-[68px] animate-pulse dark:bg-[rgba(255,255,255,0.03)] bg-gray-50`} />
              ) : (
                <div className={`flex items-center gap-4 rounded-xl ${BORDER} px-4 py-3.5`}>
                  <div className="flex-1 min-w-0">
                    <p className={`${STRONG} text-[15px] font-medium`}>Secondary Camera</p>
                    <p className={`${MUTED} text-sm mt-0.5`}>
                      {secondaryCamera.subline || 'captures, no disconnections'}
                    </p>
                  </div>
                  {/* Neutral on purpose: a capture count is not a violation. */}
                  <span className={`${STRONG} text-xl font-medium tabular-nums`}>
                    {secondaryCamera.count.toString().padStart(2, '0')}
                  </span>
                  {secondaryCamera.onOpen ? (
                    <button type="button" onClick={secondaryCamera.onOpen} className={BUTTON}>
                      See Details
                    </button>
                  ) : (
                    <span aria-hidden="true" className={`${BUTTON} invisible`}>
                      See Details
                    </span>
                  )}
                </div>
              ))}

            {flagged.map((type) => {
              const count = violationCounts[type] || 0;
              const note = spreadNote(violationLogs, type);
              const shots = (screenshotsFor && screenshotsFor(type)) || [];
              return (
                <div
                  key={type}
                  className={`flex items-center gap-4 rounded-xl ${BORDER} px-4 py-3.5`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`${STRONG} text-[15px] font-medium`}>
                      {formatViolationType(type)}
                    </p>
                    <p className={`${MUTED} text-sm mt-0.5`}>
                      {count} event{count === 1 ? '' : 's'}
                      {note && ` · ${note}`}
                    </p>
                  </div>
                  <span className="text-xl font-medium text-[#FF3030] tabular-nums">
                    {count.toString().padStart(2, '0')}
                  </span>
                  {shots.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => onReview && onReview(type, shots)}
                      className={BUTTON}
                    >
                      See Details
                    </button>
                  ) : (
                    // Holds the button's width so counts line up across rows.
                    <span aria-hidden="true" className={`${BUTTON} invisible`}>
                      See Details
                    </span>
                  )}
                </div>
              );
            })}

            {clearGroups.length > 0 && (
              <div className={`${BORDER} rounded-xl px-4 py-4`}>
                <p className="flex items-center gap-2 text-sm font-medium" style={{ color: CLEAR_HEX }}>
                  <Check className="w-4 h-4" />
                  {clear.length} check{clear.length === 1 ? '' : 's'} clear
                </p>
                <div
                  className={`grid gap-x-8 gap-y-4 mt-3 ${
                    clearGroups.length > 1 ? 'sm:grid-cols-2' : ''
                  }`}
                >
                  {clearGroups.map(([group, list]) => (
                    <div key={group}>
                      <p className={`${MUTED} text-xs font-medium uppercase tracking-wider mb-2`}>
                        {group}
                      </p>
                      <ul className="flex flex-col gap-1.5">
                        {list.map((t) => {
                          const [main, rest] = CLEAR_TEXT[t] || [formatViolationType(t), ''];
                          return (
                            <li key={t} className={`${MUTED} flex items-center gap-2 text-sm`}>
                              <span
                                className="w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ backgroundColor: CLEAR_HEX }}
                              />
                              {main}
                              {rest}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unrecorded.length > 0 && (
              <p className={`${MUTED} text-sm px-1`}>
                Not recorded for this interview: {unrecorded.map((t) => formatViolationType(t)).join(', ')}.
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProctoringSummaryCard;
